import Analytics from '@/utils/analytics';
import moment from 'moment-timezone';
import React, {
  createContext,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApplicationType } from './dynamic-config';
import {
  AvailableOrderFulfillmentFieldsFragment,
  NewOrder_OrderContextQuery,
  NewOrder_OrderContextQueryVariables,
  NewOrder_OrderFieldsFragment,
  OrderDomain,
  OrderGuideSortOrder,
  OrderStatus,
} from '@/types';
import { captureException } from '@/utils/sentry';
import {
  DispatchUpdateQuantityActionArgType,
  OrderQuantityActions,
  QuantityReducerAction,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import { FocusSource, useComponentFocus } from '@/hooks/use-component-focus';
import {
  GtinMetadataFormatType,
  OrderGuideMergeStrategy,
  PoNumberDisplay,
} from '@/enums';
import { GtinSettings } from '@/utils/gtin-metadata';
import { isDefaultCart } from '@/utils/new-order';
import { ItemListOptionMetadata, ItemSortOrder } from './item-context-types';
import {
  ListDisplaySize,
  useOrderGuideSettingsContext,
} from './order-guide-settings-context';
import {
  NewOrderQuantityStateContextProvider,
  useNewOrderQuantityStateDispatchContext,
} from './new-order-quantity-state-context';
import {
  ORDER_CONTEXT_QUERY,
  useOrderContextQueryVariables,
} from './new-order-context-queries';
import { Platform } from 'react-native';
import { ResolvedPackSort } from '@/utils/pack-sort';
import {
  SearchSortOrder,
  useUserContext,
  useUserSettingsContext,
} from './user-context';
import { useActiveOrder } from '@/hooks/use-active-order';
import { useAuthStackContext } from './auth-stack-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useLimitedPerformanceEnabled } from '@/hooks/use-limited-performance';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';
import { useOfflineOrderContext } from '@/hooks/use-offline-data';
import { usePackSortConfig } from '@/hooks/use-pack-sort-config';
import { usePunchoutParams } from '@/hooks/use-punchout-params';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const DEFAULT_ORDER_DOMAINS: OrderDomain[] = [OrderDomain.Global];

// Same array every time, so consumer memos don't invalidate when multi-cart is off
const EMPTY_DRAFT_ORDERS: NewOrder_OrderFieldsFragment[] = [];
export interface ItemDisplayOptions {
  displayItemCode: boolean;
  displayItemPrices: boolean;
  gtinSettings: GtinSettings | null;
  hidePlaceholderImages: boolean;
  itemModifiersEnabled: boolean;
}

export type ItemDisplayOptionsWithSort = ItemDisplayOptions & {
  autoSelectLowestUom: boolean;
  packSortConfig: ResolvedPackSort;
};

export interface NewOrderContextType {
  activeOrderUUID: string | null;
  allowDeliveryDateEdit: boolean;
  availableOrderFulfillments: AvailableOrderFulfillmentFieldsFragment[];
  customOrderGuideEditingEnabled: boolean;
  customOrderGuideSortEditingEnabled: boolean;
  dispatchQuantityReducerAction: React.Dispatch<QuantityReducerAction>;
  dispatchQuantityUpdateAction: (
    arg: DispatchUpdateQuantityActionArgType,
  ) => void;
  draftOrder: NewOrder_OrderFieldsFragment | null;
  draftOrders: NewOrder_OrderFieldsFragment[];
  hasResponse: boolean;
  isLoading: boolean;
  itemDisplayOptions: ItemDisplayOptions;
  multiCartEnabled: boolean;
  orderDomains: OrderDomain[];
  orderGuideMergeStrategy: OrderGuideMergeStrategy;
  poNumberDisplay: PoNumberDisplay;
  quantitiesHydrated: boolean;
  refetchOfflineDraftOrder: () => Promise<void>;
  restaurantUUID: string | null;
  selectableFulfillmentOptions: AvailableOrderFulfillmentFieldsFragment[];
  setDraftOrder: React.Dispatch<
    SetStateAction<NewOrder_OrderFieldsFragment | null>
  >;
  setDraftOrderAndHydrateQuantities: (
    order: NewOrder_OrderFieldsFragment | null,
  ) => void;
  showBarcodeScanner: boolean;
  showOrderNotes: boolean;
  status: OrderStatus | null;
  supplierTimezone: string;
  supplierUUID: string | null;
  switchActiveOrder: (uuid: string | null) => Promise<void>;
}

export const NewOrderContext = createContext<NewOrderContextType | null>(null);

export const useNewOrderContext = () => {
  const context = useContext(NewOrderContext);
  if (!context) {
    throw new Error('useNewOrderContext requires a NewOrderContextProvider');
  }

  return context;
};

export const useMaybeNewOrderContext = () => useContext(NewOrderContext);

// memoize item display options, pulling directly off of new order context
// was triggering multiple re-renders in items contexts when quantity state changes
export const useItemDisplayOptions = () => {
  const {
    itemDisplayOptions: {
      displayItemPrices,
      displayItemCode,
      gtinSettings,
      hidePlaceholderImages,
      itemModifiersEnabled,
    },
  } = useNewOrderContext();

  const packSortConfig = usePackSortConfig();
  const autoSelectLowestUom = Boolean(
    useDynamicConfigValue('on_device_scanning_config')?.auto_select_lowest_uom,
  );

  return useMemo(() => {
    return {
      autoSelectLowestUom,
      displayItemCode,
      displayItemPrices,
      gtinSettings,
      hidePlaceholderImages,
      itemModifiersEnabled,
      packSortConfig,
    };
  }, [
    autoSelectLowestUom,
    displayItemCode,
    displayItemPrices,
    gtinSettings,
    hidePlaceholderImages,
    itemModifiersEnabled,
    packSortConfig,
  ]);
};

// ListDisplaySize is Standard in the catalog, and only changes in the order guide
// so use value passed in instead of OG settings context value
export const useIsPhotoHidden = (
  metadata: ItemListOptionMetadata,
  listDisplaySize = ListDisplaySize.Standard,
) => {
  const newOrderContext = useMaybeNewOrderContext();
  // if outside of order context, don't display images. Only impacts sort order guide screen
  if (!newOrderContext) {
    return true;
  }

  const { hidePlaceholderImages } = newOrderContext.itemDisplayOptions;

  if (listDisplaySize === ListDisplaySize.Condensed) {
    return true;
  }
  return !metadata.photoURLs.length && hidePlaceholderImages;
};

interface Props {
  children?: React.ReactNode;
  multiCartSupported?: boolean;
  orderDomain?: OrderDomain;
  skipRefetchOnFocus?: boolean;
  trackLoading?: boolean;
}

export const NewOrderContextProvider: React.FC<Props> = ({
  children,
  multiCartSupported = false,
  orderDomain = OrderDomain.Global,
  trackLoading = false,
  skipRefetchOnFocus = false,
}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: {
      hidePlaceholderImages,
      supplierUUID,
      displayItemPrices,
    },
  } = useUserContext();
  const {
    catalogSortOrder,
    setCatalogSortOrder,
    searchSortOrder,
    setSearchSortOrder,
  } = useUserSettingsContext();
  const { orderGuideSortOrder, setOrderGuideSortOrder } =
    useOrderGuideSettingsContext();
  const { offlineOrderContext, syncOfflineOrderContextWithOnlineChange } =
    useOfflineOrderContext(restaurantUUID, supplierUUID);

  const dispatchQuantityState = useNewOrderQuantityStateDispatchContext();
  const limitedPerformanceEnabled = useLimitedPerformanceEnabled();

  const [draftOrder, setDraftOrder] =
    useState<NewOrder_OrderFieldsFragment | null>(null);
  const [quantitiesHydrated, setQuantitiesHydrated] = useState(false);

  // Ref so the hydration effect can read the draft order without re-running
  const draftOrderRef = useRef(draftOrder);
  useEffect(() => {
    draftOrderRef.current = draftOrder;
  }, [draftOrder]);

  const isOfflineModeActive = useOfflineModeActive();
  const isOfflineModeEnabled = useOfflineModeEnabled();
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;
  const isPunchoutSession = usePunchoutParams() !== null;
  const multiCartEnabled = Boolean(
    useDynamicConfigValue('storefront_multi_cart_enabled', false) &&
      multiCartSupported &&
      orderDomain === OrderDomain.Global &&
      !isOfflineModeActive &&
      !isPunchoutSession,
  );

  const orderDomains = useMemo(
    () =>
      multiCartEnabled
        ? [OrderDomain.Global, OrderDomain.Storefront]
        : [orderDomain],
    [multiCartEnabled, orderDomain],
  );

  const orderContextQueryVariables = useOrderContextQueryVariables(
    orderDomains,
    restaurantUUID!,
    supplierUUID!,
  );
  const query = useWrappedQuery<
    NewOrder_OrderContextQuery,
    NewOrder_OrderContextQueryVariables
  >(ORDER_CONTEXT_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: trackLoading,
    skip:
      !restaurantUUID ||
      !supplierUUID ||
      isOfflineModeActive ||
      isCatalogLimited,
    variables: orderContextQueryVariables,
  });

  const queryResult = useMemo(() => {
    if (isOfflineModeActive && offlineOrderContext.data) {
      return offlineOrderContext;
    }
    return query;
  }, [isOfflineModeActive, offlineOrderContext, query]);

  const setDraftOrderAndHydrateQuantities = useCallback(
    (order: NewOrder_OrderFieldsFragment | null, isCartSwitch = false) => {
      const leavesPreviousCart =
        multiCartEnabled &&
        (isCartSwitch ||
          (draftOrderRef.current !== null &&
            draftOrderRef.current.uuid !== order?.uuid));
      if (!order || leavesPreviousCart) {
        dispatchQuantityState({
          payload: { clearSaved: true },
          type: OrderQuantityActions.CLEAR,
        });
      }
      if (order) {
        dispatchQuantityState({
          payload: { items: order.orders_items },
          type: OrderQuantityActions.HYDRATE,
        });
      }
      setDraftOrder(order);
    },
    [dispatchQuantityState, multiCartEnabled],
  );

  const draftOrders = useMemo<NewOrder_OrderFieldsFragment[]>(() => {
    if (!multiCartEnabled) {
      return EMPTY_DRAFT_ORDERS;
    }
    return ((query.data ?? query.previousData)?.getOrders ?? []).reduce<
      NewOrder_OrderFieldsFragment[]
    >((acc, entry) => {
      if (entry.order) {
        acc.push(entry.order);
      }
      return acc;
    }, []);
  }, [multiCartEnabled, query.data, query.previousData]);

  const activeChat = query.data?.businesses_by_pk?.supplier_chats[0] ?? null;
  const activeChatUUID = activeChat?.uuid ?? null;

  const { activeOrderUUID, switchActiveOrder } = useActiveOrder({
    activeChatUUID,
    storefrontActiveOrderUUID:
      activeChat?.chats_employees?.[0]?.storefront_active_order_uuid ?? null,
  });

  const lastSelectedOrderUUIDRef = useRef<string | null>(null);
  useEffect(() => {
    // A failed request leaves `data` undefined. Don't treat it as an empty
    // cart since clearing would wipe unsynced local quantity state
    if (!queryResult.data) {
      return;
    }

    const orders = queryResult.data.getOrders;

    const pendingDraftOrder = multiCartEnabled
      ? ((activeOrderUUID
          ? orders.find((entry) => entry.order?.uuid === activeOrderUUID)?.order
          : null) ??
        orders.find((entry) => entry.order && isDefaultCart(entry.order))
          ?.order ??
        null)
      : (orders[0]?.order ?? null);
    const isCartSwitch =
      multiCartEnabled && lastSelectedOrderUUIDRef.current !== activeOrderUUID;
    lastSelectedOrderUUIDRef.current = activeOrderUUID;
    // Only clear if a draft order existed, otherwise unsynced local quantity
    // state would be wiped before it's ever saved
    if (pendingDraftOrder || draftOrderRef.current) {
      setDraftOrderAndHydrateQuantities(pendingDraftOrder, isCartSwitch);
    }
    // Set even with no draft order, since 0 is correct when there's no cart
    setQuantitiesHydrated(true);

    // Sync the online order context changes to offline mode, as long as
    // the offline data exists and we're online (as to avoid infinite loop)
    if (!isOfflineModeActive && isOfflineModeEnabled) {
      syncOfflineOrderContextWithOnlineChange(queryResult.data);
    }
  }, [
    activeOrderUUID,
    multiCartEnabled,
    setDraftOrderAndHydrateQuantities,
    isOfflineModeActive,
    isOfflineModeEnabled,
    queryResult.data,
    syncOfflineOrderContextWithOnlineChange,
  ]);

  const draftOrderUUID = draftOrder?.uuid;

  useEffect(() => {
    if (draftOrderUUID) {
      Analytics.track('new_order_context.draft_order.create', {
        orderUUID: draftOrderUUID,
      });
    } else {
      Analytics.track('new_order_context.draft_order.clear');
    }
  }, [draftOrderUUID]);

  // Refetch order context query whenever the app receives focus
  useComponentFocus({
    onFocus: useCallback(
      async (type: FocusSource) => {
        if (!skipRefetchOnFocus && type === FocusSource.App) {
          if (restaurantUUID && supplierUUID) {
            try {
              await queryResult.refetch();
            } catch (e) {
              captureException(e);
            }
          } else {
            captureException(
              new Error('OrderContext focused without restaurant or supplier'),
              {
                extra: {
                  restaurantUUID,
                  supplierUUID,
                },
              },
            );
          }
        }
      },
      [restaurantUUID, supplierUUID, queryResult, skipRefetchOnFocus],
    ),
  });

  /*
   * Not all distributors have visible item codes. This includes distributors
   * with the same bizorg, which appear in the same app.
   * Reset the sort orders back to default so order makes sense in this edge case.
   */
  useEffect(() => {
    const supplierMetadata =
      queryResult.data?.businesses_by_pk?.supplier_metadata;
    if (!supplierMetadata || supplierMetadata.display_item_code) {
      return;
    }
    const ogItemCodeOrder =
      orderGuideSortOrder === OrderGuideSortOrder.ItemCode ||
      orderGuideSortOrder === OrderGuideSortOrder.ItemCodeReverse;

    const catalogItemCodeOrder =
      catalogSortOrder === ItemSortOrder.ItemCode ||
      catalogSortOrder === ItemSortOrder.ItemCodeReverse;

    const searchItemCodeOrder =
      searchSortOrder === SearchSortOrder.ItemCode ||
      searchSortOrder === SearchSortOrder.ItemCodeReverse;

    if (ogItemCodeOrder) {
      setOrderGuideSortOrder(OrderGuideSortOrder.Alphabetical);
    }
    if (catalogItemCodeOrder) {
      setCatalogSortOrder(ItemSortOrder.Alphabetical);
    }

    if (searchItemCodeOrder) {
      setSearchSortOrder(SearchSortOrder.Relevance);
    }
  }, [
    catalogSortOrder,
    setCatalogSortOrder,
    orderGuideSortOrder,
    setOrderGuideSortOrder,
    searchSortOrder,
    setSearchSortOrder,
    queryResult.data?.businesses_by_pk?.supplier_metadata,
  ]);

  const dispatchQuantityUpdateAction = useCallback(
    ({ payload }: DispatchUpdateQuantityActionArgType) => {
      if (!limitedPerformanceEnabled) {
        Analytics.track('draft_order.update_quantity', payload);
      }

      dispatchQuantityState({
        payload,
        type: OrderQuantityActions.UPDATE_QUANTITY,
      });
    },
    [dispatchQuantityState, limitedPerformanceEnabled],
  );

  // stabilize gtinSettings reference so it doesn't churn every time the order
  // response comes back. This will prevent consumers from having to re-compute
  // list options because itemDisplayOptions will remain unchanged
  const gtinSettingsKey = JSON.stringify(
    queryResult.data?.businesses_by_pk?.supplier_metadata?.gtin_settings ??
      null,
  );
  const gtinSettings = useMemo(
    () => JSON.parse(gtinSettingsKey) as GtinSettings | null,
    [gtinSettingsKey],
  );

  const availableOrderFulfillments = useMemo(
    () => queryResult.data?.getAvailableOrderFulfillments ?? [],
    [queryResult.data?.getAvailableOrderFulfillments],
  );
  const selectableFulfillmentOptions = useMemo(
    () =>
      availableOrderFulfillments.filter(
        (fulfillmentOption) =>
          !fulfillmentOption.primary_fulfillment_settings_id,
      ),
    [availableOrderFulfillments],
  );

  const value = useMemo<NewOrderContextType>(() => {
    const supplier = queryResult.data?.businesses_by_pk;
    const supplierChat = supplier?.supplier_chats[0];
    const supplierMetadata = supplier?.supplier_metadata;
    const orderGuideEditingEnabled =
      supplierChat?.custom_order_guide_editing_enabled ?? true;
    const mergeStrategy =
      (supplierChat?.order_guide_merge_strategy as OrderGuideMergeStrategy) ??
      (supplierMetadata?.order_guide_merge_strategy as OrderGuideMergeStrategy) ??
      OrderGuideMergeStrategy.OPERATOR_DISTRIBUTOR_PRIORITIZED_UNION;

    return {
      activeOrderUUID,
      allowDeliveryDateEdit:
        supplierMetadata?.allow_delivery_date_edit ?? false,
      availableOrderFulfillments,
      customOrderGuideEditingEnabled:
        orderGuideEditingEnabled &&
        mergeStrategy !== OrderGuideMergeStrategy.DISTRIBUTOR_ONLY &&
        !isCatalogLimited,
      customOrderGuideSortEditingEnabled:
        supplierChat?.custom_order_guide_sort_editing_enabled ?? true,
      dispatchQuantityReducerAction: dispatchQuantityState,
      dispatchQuantityUpdateAction,
      draftOrder,
      draftOrders,
      hasResponse: queryResult.hasResponse,
      isLoading: queryResult.loading,
      itemDisplayOptions: {
        displayItemCode: supplierMetadata?.display_item_code ?? false,
        displayItemPrices,
        gtinSettings,
        hidePlaceholderImages,
        itemModifiersEnabled: supplierChat?.item_modifiers_enabled ?? false,
      },
      multiCartEnabled,
      orderDomains,
      orderGuideMergeStrategy: mergeStrategy,
      poNumberDisplay: (supplierChat?.po_number_display ||
        supplierMetadata?.po_number_display ||
        PoNumberDisplay.Hide) as PoNumberDisplay,
      quantitiesHydrated,
      refetchOfflineDraftOrder: offlineOrderContext.refetch,
      restaurantUUID,
      selectableFulfillmentOptions,
      setDraftOrder,
      setDraftOrderAndHydrateQuantities,
      showBarcodeScanner:
        (Platform.OS !== 'web' && supplierMetadata?.show_upc_scanner) ?? false,
      showOrderNotes: supplierMetadata?.show_order_notes ?? false,
      status:
        ((multiCartEnabled
          ? draftOrder?.status
          : queryResult.data?.getOrders[0]?.order?.status) as OrderStatus) ||
        null,
      supplierTimezone: supplier?.address.timezone || moment.tz.guess(),
      supplierUUID,
      switchActiveOrder,
    };
  }, [
    activeOrderUUID,
    availableOrderFulfillments,
    selectableFulfillmentOptions,
    draftOrders,
    dispatchQuantityState,
    dispatchQuantityUpdateAction,
    displayItemPrices,
    draftOrder,
    gtinSettings,
    orderDomains,
    hidePlaceholderImages,
    isCatalogLimited,
    multiCartEnabled,
    offlineOrderContext.refetch,
    quantitiesHydrated,
    queryResult.data?.businesses_by_pk,
    queryResult.data?.getOrders,
    queryResult.hasResponse,
    queryResult.loading,
    restaurantUUID,
    setDraftOrderAndHydrateQuantities,
    supplierUUID,
    switchActiveOrder,
  ]);

  return (
    <NewOrderContext.Provider value={value}>
      {children}
    </NewOrderContext.Provider>
  );
};

export const DummyNewOrderContextProvider: React.FC<Props> = ({ children }) => {
  const { publicRestaurant } = useAuthStackContext();
  const {
    selectedRestaurantUUID,
    selectedRestaurantRelationship: {
      hidePlaceholderImages,
      supplierUUID,
      timezone,
    },
  } = useUserContext();
  const value: NewOrderContextType = {
    activeOrderUUID: null,
    allowDeliveryDateEdit: false,
    availableOrderFulfillments: [],
    customOrderGuideEditingEnabled: false,
    customOrderGuideSortEditingEnabled: false,
    dispatchQuantityReducerAction: () => {},
    dispatchQuantityUpdateAction: () => {},
    draftOrder: null,
    draftOrders: [],
    hasResponse: false,
    isLoading: false,
    itemDisplayOptions: {
      displayItemCode: publicRestaurant?.displayItemCode ?? false,
      displayItemPrices: false,
      gtinSettings: {
        description_source_order: [GtinMetadataFormatType.Syndigo],
        name_source: GtinMetadataFormatType.Supplier,
        photo_source_order: [GtinMetadataFormatType.Syndigo],
      },
      hidePlaceholderImages,
      itemModifiersEnabled: false,
    },
    multiCartEnabled: false,
    orderDomains: DEFAULT_ORDER_DOMAINS,
    orderGuideMergeStrategy:
      OrderGuideMergeStrategy.OPERATOR_DISTRIBUTOR_PRIORITIZED_UNION,
    poNumberDisplay: PoNumberDisplay.Hide,
    quantitiesHydrated: true,
    refetchOfflineDraftOrder: () => Promise.resolve(),
    restaurantUUID: selectedRestaurantUUID,
    selectableFulfillmentOptions: [],
    setDraftOrder: () => {},
    setDraftOrderAndHydrateQuantities: () => {},
    showBarcodeScanner: false,
    showOrderNotes: false,
    status: null,
    supplierTimezone: timezone,
    supplierUUID,
    switchActiveOrder: () => Promise.resolve(),
  };
  return (
    <NewOrderQuantityStateContextProvider>
      <NewOrderContext.Provider value={value}>
        {children}
      </NewOrderContext.Provider>
    </NewOrderQuantityStateContextProvider>
  );
};
